import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './Layout';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { 
  Users as UsersIcon, 
  BookOpen, 
  HelpCircle, 
  BookAIcon, 
  Search,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { userAnswerAPI, courseAPI, classAPI, userAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const Dashboard = ({ user, onLogout }) => {
  const [dashboardType, setDashboardType] = useState('individual'); // 'individual' or 'class'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [classSearchData, setClassSearchData] = useState({
    classId: ''
  });
  const [classSearchData2, setClassSearchData2] = useState({
    classId: ''
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [classDashboardData, setClassDashboardData] = useState(null);
  const [classDashboardData2, setClassDashboardData2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState(null);
  const [error2, setError2] = useState(null);
  const [selectedTab, setSelectedTab] = useState('lm-quiz');
  const [selectedIncorrectTopic, setSelectedIncorrectTopic] = useState(null);
  const [selectedClassTopic, setSelectedClassTopic] = useState(null);
  const [selectedClassTopic2, setSelectedClassTopic2] = useState(null);
  const [courseName, setCourseName] = useState('');
  const [allClasses, setAllClasses] = useState([]);

  const statsCards = [
    {
      title: 'Users',
      description: 'Manage user accounts and permissions',
      icon: UsersIcon
    },
    {
      title: 'Program',
      description: 'Create and manage program content',
      icon: BookOpen
    },
    {
      title: 'Quizzes',
      description: 'Build and monitor quiz activities',
      icon: HelpCircle
    },
    {
      title: 'Lessons',
      description: 'Manage course lessons and materials',
      icon: BookAIcon
    }
  ];

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await userAPI.getAllUsers();
        if (response.data) {
          setAllUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers([]);
      setShowDropdown(false);
      return;
    }

    const filtered = allUsers.filter(user => 
      user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered.slice(0, 10)); // Limit to 10 results
    setShowDropdown(filtered.length > 0);
  }, [searchQuery, allUsers]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchQuery(`${user.first_name} ${user.last_name}`);
    setShowDropdown(false);
  };

  const handleSearch = async () => {
    if (!selectedUser) {
      setError('Please select a user from the dropdown');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userAnswerAPI.getIndividualDashboard(selectedUser.id);
      
      console.log('API Response:', response);
      
      if (response.status === 'success') {
        console.log('Dashboard Data:', response.data);
        setDashboardData(response.data);
        
        // Fetch course name if course ID exists
        if (response.data.individual_info?.course) {
          try {
            const courses = await courseAPI.getAllCourses();
            const course = courses.data?.find(c => c.id === response.data.individual_info.course);
            if (course) {
              setCourseName(course.name || course.title || 'Unknown Course');
            }
          } catch (courseErr) {
            console.error('Error fetching course:', courseErr);
            setCourseName(`Course ID: ${response.data.individual_info.course}`);  
          }
        }
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message || 'Error fetching dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const downloadIndividualDashboard = () => {
    if (!dashboardData || !selectedUser) return;

    const workbook = XLSX.utils.book_new();

    // Student Info Sheet
    const studentInfo = [
      ['INDIVIDUAL REPORT'],
      [],
      ['Student ID', dashboardData.individual_info?.student_id || ''],
      ['Name', `${selectedUser.first_name} ${selectedUser.last_name}`],
      ['Registration No', dashboardData.individual_info?.rd || ''],
      ['Email', dashboardData.individual_info?.email || ''],
      ['Course (Level)', courseName || dashboardData.individual_info?.course || ''],
      ['Payment Made', `${formatCurrency(dashboardData.individual_info?.payment || 0)} ₮`],
      ['Payment Remaining', `${formatCurrency(dashboardData.individual_info?.payment_remaining || 0)} ₮`],
      ['Company', dashboardData.individual_info?.company || ''],
      ['Occupation', dashboardData.individual_info?.occupation || ''],
      [],
      ['Overall Average', `${dashboardData.overall_average?.toFixed(1) || 0}%`]
    ];
    const studentSheet = XLSX.utils.aoa_to_sheet(studentInfo);
    XLSX.utils.book_append_sheet(workbook, studentSheet, 'Student Info');

    // Topic Performance Sheet
    if (dashboardData.topic_performance) {
      const topicPerformanceArray = Array.isArray(dashboardData.topic_performance)
        ? dashboardData.topic_performance
        : [dashboardData.topic_performance];
      
      const topicData = [
        ['Topic', 'Abbreviation', 'Average Percentage']
      ];
      topicPerformanceArray.forEach(topic => {
        topicData.push([
          topic.topic_title || '',
          topic.topic_abbreviation || '',
          topic.average_percentage || 0
        ]);
      });
      const topicSheet = XLSX.utils.aoa_to_sheet(topicData);
      XLSX.utils.book_append_sheet(workbook, topicSheet, 'Topic Performance');
    }

    // Answer Count by Topic Sheet
    if (dashboardData.answer_count_by_topic) {
      const answerCountArray = Array.isArray(dashboardData.answer_count_by_topic)
        ? dashboardData.answer_count_by_topic
        : [dashboardData.answer_count_by_topic];
      
      const answerData = [
        ['Topic', 'Abbreviation', 'Answer Count', 'Total Points']
      ];
      answerCountArray.forEach(topic => {
        answerData.push([
          topic.topic_title || '',
          topic.topic_abbreviation || '',
          topic.answer_count || 0,
          topic.total_points || 0
        ]);
      });
      const answerSheet = XLSX.utils.aoa_to_sheet(answerData);
      XLSX.utils.book_append_sheet(workbook, answerSheet, 'Answer Count');
    }

    // Lesson Summary Sheet
    if (dashboardData.lesson_summary) {
      const lessonSummaryArray = Array.isArray(dashboardData.lesson_summary)
        ? dashboardData.lesson_summary
        : [dashboardData.lesson_summary];
      
      const lessonData = [
        ['Topic', 'Lesson', 'Total Answers', 'Correct Answers', 'Incorrect Answers', 'Accuracy Rate (%)']
      ];
      lessonSummaryArray.forEach(lesson => {
        lessonData.push([
          lesson.topic_title || '',
          lesson.lesson_title || '',
          lesson.total_answers || 0,
          lesson.correct_answers || 0,
          lesson.incorrect_answers || 0,
          lesson.accuracy_rate || 0
        ]);
      });
      const lessonSheet = XLSX.utils.aoa_to_sheet(lessonData);
      XLSX.utils.book_append_sheet(workbook, lessonSheet, 'Lesson Summary');
    }

    // Exam Performance Sheet
    if (dashboardData.exam_performance) {
      const examPerformanceArray = Array.isArray(dashboardData.exam_performance)
        ? dashboardData.exam_performance
        : [dashboardData.exam_performance];
      
      const examData = [
        ['Exam Type', 'Correct Answers', 'Incorrect Answers', 'Absent', 'Total Questions', 'Percentile (%)']
      ];
      examPerformanceArray.forEach(exam => {
        examData.push([
          exam.exam_type_name || exam.exam_type_abbreviation || '',
          exam.correct_answers || 0,
          exam.incorrect_answers || 0,
          exam.absent || 0,
          exam.total_questions || 0,
          (exam.percentile * 100).toFixed(1)
        ]);
      });
      const examSheet = XLSX.utils.aoa_to_sheet(examData);
      XLSX.utils.book_append_sheet(workbook, examSheet, 'Exam Performance');
    }

    // Incorrect Answers Sheet
    if (dashboardData.incorrect_answers) {
      const incorrectAnswersArray = Array.isArray(dashboardData.incorrect_answers)
        ? dashboardData.incorrect_answers
        : [dashboardData.incorrect_answers];
      
      const incorrectData = [
        ['#', 'Topic', 'Question', 'Point', 'Correct Answer', 'LM', 'LOS']
      ];
      incorrectAnswersArray.forEach((answer, index) => {
        incorrectData.push([
          index + 1,
          answer.topic_abbreviation || answer.topic_title || '',
          answer.question_text ? answer.question_text.replace(/<[^>]*>/g, '') : '', // Remove HTML tags
          answer.point || 0,
          answer.answer_options?.filter(option => option.is_correct === 1)[0]?.option_text || '',
          answer.lesson_title || answer.topic_title || '',
          answer.los_name ? answer.los_name.split(':')[0] : ''
        ]);
      });
      const incorrectSheet = XLSX.utils.aoa_to_sheet(incorrectData);
      XLSX.utils.book_append_sheet(workbook, incorrectSheet, 'Incorrect Answers');
    }

    // Generate filename and download
    const fileName = `Individual_Dashboard_${selectedUser.first_name}_${selectedUser.last_name}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const downloadClassDashboard = (data, classNumber) => {
    if (!data) return;

    const workbook = XLSX.utils.book_new();

    // Class Info Sheet
    const classInfo = [
      [`CLASS ${classNumber} DASHBOARD`],
      [],
      ['Class ID', data.class_info?.class_id || ''],
      ['Class Name', data.class_info?.class_name || ''],
      [],
      ['ATTENDANCE STATISTICS'],
      ['Total Students', data.attendance?.total_students || 0],
      ['Students Attended', data.attendance?.students_attended || 0],
      ['Attendance Rate (%)', data.attendance?.attendance_rate || 0]
    ];
    const classSheet = XLSX.utils.aoa_to_sheet(classInfo);
    XLSX.utils.book_append_sheet(workbook, classSheet, 'Class Info');

    // Topic Performance Sheet
    if (data.topic_performance) {
      const topicPerformanceArray = Array.isArray(data.topic_performance)
        ? data.topic_performance
        : [data.topic_performance];
      
      const topicData = [
        ['Topic', 'Class Score (%)', 'Overall Average (%)', 'Difference (%)']
      ];
      topicPerformanceArray.forEach(topic => {
        const classScore = (topic.average_score * 100).toFixed(1);
        const overallAverage = (topic.class_average_score * 100).toFixed(1);
        const diff = (classScore - overallAverage).toFixed(1);
        topicData.push([
          topic.topic_title || '',
          classScore,
          overallAverage,
          diff
        ]);
      });
      const topicSheet = XLSX.utils.aoa_to_sheet(topicData);
      XLSX.utils.book_append_sheet(workbook, topicSheet, 'Topic Performance');
    }

    // User Answers Summary Sheet
    if (data.user_answers_summary) {
      const userAnswersArray = Array.isArray(data.user_answers_summary)
        ? data.user_answers_summary
        : [data.user_answers_summary];
      
      const answerData = [
        ['Topic', 'Lesson', 'Total Answers', 'Correct Answers', 'Incorrect Answers', 'Accuracy Rate (%)']
      ];
      userAnswersArray.forEach(lesson => {
        answerData.push([
          lesson.topic_title || '',
          lesson.lesson_title || '',
          lesson.total_answers || 0,
          lesson.correct_answers || 0,
          lesson.incorrect_answers || 0,
          lesson.accuracy_rate || 0
        ]);
      });
      const answerSheet = XLSX.utils.aoa_to_sheet(answerData);
      XLSX.utils.book_append_sheet(workbook, answerSheet, 'User Answers Summary');
    }

    // Generate filename and download
    const fileName = `Class_${classNumber}_Dashboard_${data.class_info?.class_name || 'Unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getQuizColor = (quiz) => {
    const colors = ['#1e40af', '#059669', '#7c3aed', '#dc2626', '#f59e0b', '#0891b2', '#4f46e5', '#be123c', '#ca8a04'];
    const quizNames = ['QM', 'ECN', 'CI', 'FSA', 'EQ', 'FI', 'DER', 'ALT', 'PM', 'ETC'];
    const index = quizNames.indexOf(quiz);
    return colors[index] || '#6b7280';
  };

  const renderStudentInfo = () => {
    if (!dashboardData) return null;

    const { individual_info } = dashboardData;

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold mb-4 text-gray-800">INDIVIDUAL REPORT</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              <span className="font-semibold">Student:</span>
              <select className="ml-2 border border-gray-300 rounded px-2 py-1">
                <option>{individual_info.student_id} {selectedUser?.last_name || ''} {selectedUser?.first_name || ''}</option>
              </select>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Student ID:</span>
              <span className="ml-2">{individual_info.student_id}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Registration No:</span>
              <span className="ml-2">{individual_info.rd}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Email: </span>
              <span className="ml-2">{individual_info.email}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Course (Level):</span>
              <span className="ml-2">{courseName || individual_info.course}</span>
            </div>
          </div>
          <div>
            <div className="mb-2">
              <span className="font-semibold">Payment Made:</span>
              <span className="ml-2">{formatCurrency(individual_info.payment) + ' ₮'}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Payment Remaining:</span>
              <span className="ml-2">{formatCurrency(individual_info.payment_remaining) + ' ₮'}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Company:</span>
              <span className="ml-2">{individual_info.company}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Occupation:</span>
              <span className="ml-2">{individual_info.occupation}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAttendanceChart = () => {
    if (!dashboardData || !dashboardData.topic_performance) return null;

    const topicPerformanceArray = Array.isArray(dashboardData.topic_performance) 
      ? dashboardData.topic_performance 
      : [dashboardData.topic_performance];

    const chartData = topicPerformanceArray.map(topic => ({
      name: topic.topic_abbreviation || topic.topic_title?.substring(0, 3).toUpperCase() || 'N/A',
      percentage: topic.average_percentage || 0
    }));

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-2">Topic Performance</h3>
        <p className="text-2xl font-bold mb-4">
          Average Percentage {dashboardData.overall_average?.toFixed(1)}%
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="percentage" fill="#3b82f6" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getQuizColor(entry.name)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderAnswerCountByTopic = () => {
    if (!dashboardData || !dashboardData.answer_count_by_topic) return null;

    const answerCountArray = Array.isArray(dashboardData.answer_count_by_topic)
      ? dashboardData.answer_count_by_topic
      : [dashboardData.answer_count_by_topic];

    const chartData = answerCountArray.map(topic => ({
      name: topic.topic_abbreviation || topic.topic_title?.substring(0, 10) || 'Topic',
      answers: topic.answer_count || 0,
      points: topic.total_points || 0
    }));

    // Calculate dynamic YAxis domain based on data
    const allValues = chartData.flatMap(item => [item.answers, item.points]);
    const maxValue = Math.max(...allValues);
    const yAxisMax = Math.ceil(maxValue / 10) * 10 + 10; // Round up to nearest 10 and add padding

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-4">Answer Count by Topic</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, yAxisMax]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="answers" fill="#3b82f6" name="Total Answers" radius={[8, 8, 0, 0]} />
            <Bar dataKey="points" fill="#10b981" name="Correct Answers" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderExamPerformanceChart = () => {
    if (!dashboardData || !dashboardData.exam_performance) return null;

    const examPerformanceArray = dashboardData.exam_performance 
      ? (Array.isArray(dashboardData.exam_performance) 
          ? dashboardData.exam_performance 
          : [dashboardData.exam_performance])
      : [];

    if (examPerformanceArray.length === 0) return null;

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-4">Exam Performance Overview</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-4 text-left font-semibold">Exam Type</th>
                <th className="border p-4 text-center font-semibold">Percentile</th>
              </tr>
            </thead>
            <tbody>
              {examPerformanceArray.map((exam, index) => {
                const percentile = (exam.percentile * 100).toFixed(1);
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border p-4 font-medium text-gray-800">
                      {exam.exam_type_name || exam.exam_type_abbreviation || 'N/A'}
                    </td>
                    <td className="border p-4 text-center">
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                        percentile >= 70 ? 'bg-green-100 text-green-800' :
                        percentile >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {percentile}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuizTables = () => {
    if (!dashboardData) return null;

    const quizTypes = [
      { id: 'lm-quiz', title: 'LM Quiz', label: 'LM QUIZ' },
      { id: 'topic-quiz', title: 'Topic Quiz', label: 'Topic Quiz' },
      { id: 'half-mock', title: 'Half Mock', label: 'Half Mock' },
      { id: 'full-mock', title: 'Full Mock', label: 'Full mock' }
    ];

    // Handle exam_performance - convert to array if it's an object
    const examPerformanceArray = dashboardData.exam_performance 
      ? (Array.isArray(dashboardData.exam_performance) 
          ? dashboardData.exam_performance 
          : [dashboardData.exam_performance])
      : [];

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex space-x-2 mb-4">
          {quizTypes.map(quiz => (
            <button
              key={quiz.id}
              onClick={() => setSelectedTab(quiz.id)}
              className={`px-4 py-2 rounded font-medium ${
                selectedTab === quiz.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {quiz.title}
            </button>
          ))}
        </div>

        {examPerformanceArray.length > 0 && (
          <div className="mb-4">
            <div className="grid grid-cols-5 gap-4 font-semibold border-b pb-2">
              <div>Student Answer</div>
              <div>Correct Answer</div>
              <div>Incorrect Answer</div>
              <div>Absent</div>
              <div>Total Question</div>
            </div>
            {examPerformanceArray.map((exam, index) => (
              <div key={index} className="grid grid-cols-5 gap-4 py-2 border-b">
                <div className="font-medium">{exam.exam_type_abbreviation || exam.exam_type_name}</div>
                <div className="text-center">{exam.correct_answers || 0}</div>
                <div className="text-center">{exam.incorrect_answers || 0}</div>
                <div className="text-center">{exam.absent || 0}</div>
                <div className="text-center">{exam.total_questions || 0}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <p className="font-semibold mb-2">
            {selectedTab === 'lm-quiz' && `LM Quiz: 64%`}
            {selectedTab === 'topic-quiz' && `Topic Quiz: 0%`}
            {selectedTab === 'half-mock' && `Half Mock: 0%`}
            {selectedTab === 'full-mock' && `Full Mock: 0%`}
          </p>
          <p className="text-sm text-gray-600 italic">
            {selectedTab === 'lm-quiz' && `Percentile by LM Quiz`}
            {selectedTab === 'topic-quiz' && `Percentile by Topic Quiz`}
            {selectedTab === 'half-mock' && `Percentile by Half Mock`}
            {selectedTab === 'full-mock' && `Percentile by Full Mock`}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            <span className="font-semibold">Worst Topic:</span> 
            {dashboardData.worst_topic && ` ${dashboardData.worst_topic.topic_title} (${dashboardData.worst_topic.percentage}%)`}
          </p>
        </div>
      </div>
    );
  };

  const renderIncorrectQuestions = () => {
    if (!dashboardData || !dashboardData.incorrect_answers) return null;

    const incorrectAnswersArray = Array.isArray(dashboardData.incorrect_answers)
      ? dashboardData.incorrect_answers
      : [dashboardData.incorrect_answers];

    if (incorrectAnswersArray.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Incorrect Questions</h3>
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="w-16 h-16 mx-auto mb-2 text-green-500" />
            <p>No incorrect answers found. Great job!</p>
          </div>
        </div>
      );
    }

    // Group questions by topic for better visualization
    const groupedByTopic = {};
    incorrectAnswersArray.forEach(answer => {
      const topic = answer.topic_abbreviation || answer.topic_title || 'Unknown';
      if (!groupedByTopic[topic]) {
        groupedByTopic[topic] = [];
      }
      groupedByTopic[topic].push(answer);
    });

    // Initialize selected topic if not set or if it doesn't exist in current data
    const availableTopics = Object.keys(groupedByTopic);
    const currentSelectedTopic = selectedIncorrectTopic && availableTopics.includes(selectedIncorrectTopic) 
      ? selectedIncorrectTopic 
      : availableTopics[0];

    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold">Incorrect Questions Analysis</h3>
            <p className="text-sm text-gray-600 mt-1">
              Total incorrect answers: <span className="font-semibold text-red-600">{incorrectAnswersArray.length}</span>
            </p>
          </div>
        </div>
        
        {/* Topic Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.keys(groupedByTopic).map(topic => (
            <button
              key={topic}
              onClick={() => setSelectedIncorrectTopic(topic)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentSelectedTopic === topic 
                  ? 'text-white shadow-lg transform scale-105' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={currentSelectedTopic === topic ? { backgroundColor: getQuizColor(topic) } : {}}
            >
              {topic}
              <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-30 rounded-full text-xs">
                {groupedByTopic[topic].length}
              </span>
            </button>
          ))}
        </div>

        {/* Lesson Summary for Selected Topic */}
        {dashboardData.lesson_summary && (() => {
          const lessonSummaryArray = Array.isArray(dashboardData.lesson_summary)
            ? dashboardData.lesson_summary
            : [dashboardData.lesson_summary];
          
          // Filter lessons by selected topic
          const filteredLessons = lessonSummaryArray.filter(lesson => {
            const topicMatch = lesson.topic_abbreviation || lesson.topic_title || 'Unknown';
            return topicMatch === currentSelectedTopic;
          });

          if (filteredLessons.length > 0) {
            return (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <h4 className="text-md font-semibold mb-4 text-blue-900">Lesson Performance - {currentSelectedTopic}</h4>
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lesson</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Correct</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Incorrect</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLessons.map((lesson, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{lesson.lesson_title}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {lesson.total_answers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {lesson.correct_answers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {lesson.incorrect_answers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    lesson.accuracy_rate >= 70 ? 'bg-green-500' :
                                    lesson.accuracy_rate >= 50 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${lesson.accuracy_rate}%` }}
                                ></div>
                              </div>
                              <span className={`font-semibold text-sm ${
                                lesson.accuracy_rate >= 70 ? 'text-green-600' :
                                lesson.accuracy_rate >= 50 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {lesson.accuracy_rate}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Total Incorrect</p>
                <p className="text-2xl font-bold text-red-700">{incorrectAnswersArray.length}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Topics Affected</p>
                <p className="text-2xl font-bold text-blue-700">{Object.keys(groupedByTopic).length}</p>
              </div>
              <BookOpen className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Most Errors</p>
                <p className="text-lg font-bold text-yellow-700 truncate">
                  {Object.keys(groupedByTopic).reduce((a, b) => 
                    groupedByTopic[a].length > groupedByTopic[b].length ? a : b
                  )}
                </p>
              </div>
              <AlertCircle className="w-10 h-10 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Detailed Table for Selected Topic */}
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <h4 className="text-md font-semibold mb-3" style={{ color: getQuizColor(currentSelectedTopic) }}>
            {currentSelectedTopic} - Incorrect Questions Detail
          </h4>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-3 text-left font-semibold">#</th>
                <th className="border p-3 text-left font-semibold">Question</th>
                <th className="border p-3 text-left font-semibold">Point</th>
                <th className="border p-3 text-left font-semibold">Correct Answer</th>
                <th className="border p-3 text-left font-semibold">LM</th>
                <th className="border p-3 text-left font-semibold">LOS</th>
              </tr>
            </thead>
            <tbody>
              {groupedByTopic[currentSelectedTopic].map((answer, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="border p-3 text-gray-600">{index + 1}</td>
                  <td className="border p-3 font-medium text-blue-600 w-1/2">
                    <div dangerouslySetInnerHTML={{ __html: answer.question_text || 'N/A' }} />
                  </td>
                  <td className="border p-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      answer.point > 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {answer.point}
                    </span>
                  </td>
                  <td className="border p-3">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {answer.answer_options.filter(option => option.is_correct === 1)[0]?.option_text || 'N/A'}
                    </span>
                  </td>
                  <td className="border p-3 text-gray-700">
                    {answer.lesson_title || answer.topic_title || 'N/A'}
                  </td>
                  <td className="border p-3 text-gray-700">
                    {answer.los_name ? answer.los_name.split(':')[0] : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Chart Visualization */}
        {/* <div className="mt-6">
          <h4 className="text-md font-semibold mb-3">Incorrect Answers by Topic</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={Object.keys(groupedByTopic).map(topic => ({
              name: topic,
              count: groupedByTopic[topic].length
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#ef4444" radius={[8, 8, 0, 0]}>
                {Object.keys(groupedByTopic).map((topic, index) => (  
                  <Cell key={`cell-${index}`} fill={getQuizColor(topic)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div> */}
      </div>
    );
  };

  const renderLessonSummary = () => {
    if (!dashboardData || !dashboardData.lesson_summary) return null;

    const lessonSummaryArray = Array.isArray(dashboardData.lesson_summary)
      ? dashboardData.lesson_summary
      : [dashboardData.lesson_summary];

    if (lessonSummaryArray.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-lg font-semibold mb-4">Lesson Summary</h3>
          <div className="text-center py-8 text-gray-500">
            <BookOpen className="w-16 h-16 mx-auto mb-2 text-gray-400" />
            <p>No lesson data available.</p>
          </div>
        </div>
      );
    }

    // Group lessons by topic
    const groupedByTopic = {};
    lessonSummaryArray.forEach(lesson => {
      const topic = lesson.topic_title || 'Unknown Topic';
      if (!groupedByTopic[topic]) {
        groupedByTopic[topic] = [];
      }
      groupedByTopic[topic].push(lesson);
    });

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold">Lesson Performance Summary</h3>
            <p className="text-sm text-gray-600 mt-1">
              Detailed breakdown of performance by lesson
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Lessons</p>
            <p className="text-2xl font-bold text-blue-600">{lessonSummaryArray.length}</p>
          </div>
        </div>

        <div className="space-y-6">
          {Object.keys(groupedByTopic).map((topicTitle, topicIndex) => (
            <div key={topicIndex} className="border rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b">
                <h4 className="font-semibold text-blue-900">{topicTitle}</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {groupedByTopic[topicTitle].length} lesson{groupedByTopic[topicTitle].length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lesson</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Correct</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Incorrect</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByTopic[topicTitle].map((lesson, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{lesson.lesson_title}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {lesson.total_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {lesson.correct_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {lesson.incorrect_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  lesson.accuracy_rate >= 70 ? 'bg-green-500' :
                                  lesson.accuracy_rate >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${lesson.accuracy_rate}%` }}
                              ></div>
                            </div>
                            <span className={`font-semibold text-sm ${
                              lesson.accuracy_rate >= 70 ? 'text-green-600' :
                              lesson.accuracy_rate >= 50 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {lesson.accuracy_rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Class Dashboard Functions
  useEffect(() => {
    const fetchAllClasses = async () => {
      try {
        const response = await classAPI.getAllClasses();
        if (response.data) {
          setAllClasses(response.data);
        }
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
    };
    fetchAllClasses();
  }, []);

  const handleClassSearch = async () => {
    if (!classSearchData.classId) {
      setError('Please select a class');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await classAPI.getClassDashboard(classSearchData.classId);
      
      console.log('Class Dashboard Response:', response);
      
      if (response.status === 'success') {
        console.log('Class Dashboard Data:', response.data);
        setClassDashboardData(response.data);
      } else {
        setError('Failed to fetch class dashboard data');
      }
    } catch (err) {
      console.error('Error fetching class dashboard:', err);
      setError(err.message || 'Error fetching class dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleClassSearch2 = async () => {
    if (!classSearchData2.classId) {
      setError2('Please select a class');
      return;
    }

    setLoading2(true);
    setError2(null);

    try {
      const response = await classAPI.getClassDashboard(classSearchData2.classId);
      
      console.log('Class Dashboard 2 Response:', response);
      
      if (response.status === 'success') {
        console.log('Class Dashboard 2 Data:', response.data);
        setClassDashboardData2(response.data);
      } else {
        setError2('Failed to fetch class dashboard data');
      }
    } catch (err) {
      console.error('Error fetching class dashboard 2:', err);
      setError2(err.message || 'Error fetching class dashboard data');
    } finally {
      setLoading2(false);
    }
  };

  const renderClassInfo = (data, title = 'CLASS DASHBOARD') => {
    if (!data) return null;

    const { class_info, attendance } = data;

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold mb-4 text-gray-800">{title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Class Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Class ID:</span>
                <span className="text-gray-800">{class_info.class_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Class Name:</span>
                <span className="text-gray-800">{class_info.class_name}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Attendance Statistics</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Total Students:</span>
                <span className="text-gray-800 font-semibold">{attendance.total_students}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Students Attended:</span>
                <span className="text-green-600 font-semibold">{attendance.students_attended}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Attendance Rate:</span>
                <span className={`font-bold text-lg ${
                  attendance.attendance_rate >= 80 ? 'text-green-600' :
                  attendance.attendance_rate >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {(attendance.attendance_rate || 0).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTopicPerformanceChart = (data) => {
    if (!data || !data.topic_performance) return null;

    const topicPerformanceArray = Array.isArray(data.topic_performance)
      ? data.topic_performance
      : [data.topic_performance];

    if (topicPerformanceArray.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6 text-center">
          <p className="text-gray-600">No topic performance data available for this class.</p>
        </div>
      );
    }

    const chartData = topicPerformanceArray.map(topic => ({
      name: topic.topic_title?.substring(0, 15) || 'Topic',
      ' ': parseFloat((topic.average_score * 100).toFixed(1)),
      'Overall Average': parseFloat((topic.class_average_score * 100).toFixed(1))
    }));

    // Calculate dynamic YAxis domain based on data
    const allScores = chartData.flatMap(item => [item['Class Score'], item['Overall Average']]);
    const maxScore = Math.max(...allScores);
    const minScore = Math.min(...allScores);
    const yAxisMax = Math.ceil(maxScore / 10) * 10 + 10; // Round up to nearest 10 and add padding
    const yAxisMin = Math.max(0, Math.floor(minScore / 10) * 10 - 10); // Round down to nearest 10 with padding, but not below 0

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-4">Topic Performance Comparison</h3>
        <p className="text-sm text-gray-600 mb-4">
          Compare this class's performance against overall class averages
        </p>
        
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis domain={[yAxisMin, yAxisMax]} label={{ value: 'Score %', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => `${value}%`} />
            <Legend />
            <Bar dataKey="Class Score" fill="#3b82f6" radius={[8, 8, 0, 0]} />
            <Line dataKey="Overall Average" stroke="#10b981" strokeWidth={4 } />
          </BarChart>
        </ResponsiveContainer>

        {/* Detailed Table */}
        <div className="mt-6">
          <h4 className="text-sm font-medium mb-3 text-gray-600">Detailed Performance Breakdown</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-3 text-left">Topic</th>
                  <th className="border p-3 text-center">Class Score</th>
                  <th className="border p-3 text-center">Overall Average</th>
                  <th className="border p-3 text-center">Performance</th>
                </tr>
              </thead>
              <tbody>
                {topicPerformanceArray.map((topic, index) => {
                  const classScore = (topic.average_score * 100).toFixed(1);
                  const overallAverage = (topic.class_average_score * 100).toFixed(1);
                  const diff = (classScore - overallAverage).toFixed(1);
                  const isAboveAverage = diff > 0;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border p-3 font-medium">{topic.topic_title}</td>
                      <td className="border p-3 text-center">
                        <span className="font-semibold text-blue-600">{classScore}%</span>
                      </td>
                      <td className="border p-3 text-center">
                        <span className="font-semibold text-green-600">{overallAverage}%</span>
                      </td>
                      <td className="border p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                          isAboveAverage 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {isAboveAverage ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                          {isAboveAverage ? '+' : ''}{diff}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderClassUserAnswersSummary = (data, classNumber = 1) => {
    if (!data || !data.user_answers_summary) return null;

    const userAnswersArray = Array.isArray(data.user_answers_summary)
      ? data.user_answers_summary
      : [data.user_answers_summary];

    if (userAnswersArray.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-lg font-semibold mb-4">User Answers Summary</h3>
          <div className="text-center py-8 text-gray-500">
            <BookOpen className="w-16 h-16 mx-auto mb-2 text-gray-400" />
            <p>No user answers data available.</p>
          </div>
        </div>
      );
    }

    // Group lessons by topic
    const groupedByTopic = {};
    userAnswersArray.forEach(lesson => {
      const topic = lesson.topic_title || 'Unknown Topic';
      if (!groupedByTopic[topic]) {
        groupedByTopic[topic] = [];
      }
      groupedByTopic[topic].push(lesson);
    });

    // Get selected topic based on class number
    const availableTopics = Object.keys(groupedByTopic);
    const selectedTopic = classNumber === 1 ? selectedClassTopic : selectedClassTopic2;
    const setSelectedTopic = classNumber === 1 ? setSelectedClassTopic : setSelectedClassTopic2;
    const currentSelectedTopic = selectedTopic && availableTopics.includes(selectedTopic)
      ? selectedTopic
      : availableTopics[0];

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold">User Answers Summary</h3>
            <p className="text-sm text-gray-600 mt-1">
              Detailed breakdown of student answers by lesson
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Lessons</p>
            <p className="text-2xl font-bold text-blue-600">{userAnswersArray.length}</p>
          </div>
        </div>

        {/* Topic Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.keys(groupedByTopic).map(topic => (
            <button
              key={topic}
              onClick={() => setSelectedTopic(topic)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentSelectedTopic === topic
                  ? 'bg-gray-600 text-white shadow-lg transform scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {topic}
              <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-30 rounded-full text-xs">
                {groupedByTopic[topic].length}
              </span>
            </button>
          ))}
        </div>

        {/* Display only selected topic */}
        <div className="space-y-6">
          {currentSelectedTopic && groupedByTopic[currentSelectedTopic] && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-3 border-b">
                <h4 className="font-semibold text-blue-900">{currentSelectedTopic}</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {groupedByTopic[currentSelectedTopic].length} lesson{groupedByTopic[currentSelectedTopic].length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lesson</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Correct</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Incorrect</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByTopic[currentSelectedTopic].map((lesson, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{lesson.lesson_title}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {lesson.total_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {lesson.correct_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {lesson.incorrect_answers}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  lesson.accuracy_rate >= 70 ? 'bg-green-500' :
                                  lesson.accuracy_rate >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${lesson.accuracy_rate}%` }}
                              ></div>
                            </div>
                            <span className={`font-semibold text-sm ${
                              lesson.accuracy_rate >= 70 ? 'text-green-600' :
                              lesson.accuracy_rate >= 50 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {lesson.accuracy_rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="mb-8 ml-8">
        <h1 className="text-3xl font-semibold text-gray-800 mb-4">Admin Dashboard</h1>
        
        {/* Dashboard Type Tabs */}
        <div className="mb-6">
          <div className="flex space-x-2 border-b-2 border-gray-200">
            <button
              onClick={() => setDashboardType('individual')}
              className={`px-6 py-3 font-semibold transition-all ${
                dashboardType === 'individual'
                  ? 'border-b-4 border-blue-600 text-blue-600 -mb-0.5'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Individual Dashboard
            </button>
            <button
              onClick={() => setDashboardType('class')}
              className={`px-6 py-3 font-semibold transition-all ${
                dashboardType === 'class'
                  ? 'border-b-4 border-blue-600 text-blue-600 -mb-0.5'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Class Dashboard
            </button>
          </div>
        </div>

        {/* Individual Dashboard Content */}
        {dashboardType === 'individual' && (
          <>
            {/* Search Section */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Search Student</h2>
                {dashboardData && selectedUser && (
                  <button
                    onClick={downloadIndividualDashboard}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download size={20} />
                    Download Excel
                  </button>
                )}
              </div>
              <div className="flex gap-4 items-end">
                <div className="flex-1 relative">
                  <label className="block text-sm font-medium mb-2">Student Name</label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedUser(null);
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Type student name..."
                  />
                  {showDropdown && filteredUsers.length > 0 && !selectedUser && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          onClick={(e) => {
                            e.preventDefault();
                            handleUserSelect(user);
                          }}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="font-medium">{user.first_name} {user.last_name}</div>
                          <div className="text-sm text-gray-500">
                            {user.email || `ID: ${user.id}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  disabled={loading || !selectedUser}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
                >
                  <Search size={20} />
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {error && (
                <div className="mt-4 p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle size={20} />
                  {error}
                </div>
              )}
            </div>

            {/* Dashboard Data */}
            {dashboardData && (
              <>
                {renderStudentInfo()}
                
                {/* Topic Performance */}
                {renderAttendanceChart()}
                
                {/* Answer Count by Topic */}
                {renderAnswerCountByTopic()}
                
                {/* Exam Performance */}
                {renderExamPerformanceChart()}
                
                {/* Incorrect Questions */}
                {renderIncorrectQuestions()}
              </>
            )}

            {/* Default Cards when no search */}
            {/* {!dashboardData && !loading && (
              <>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Welcome to the ICER Admin Portal. Search for a student to view their individual performance dashboard.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {statsCards.map((card, index) => {
                    const IconComponent = card.icon;
                    return (
                      <Card 
                        key={index} 
                        className="bg-white shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 border-l-4 border-l-blue-500"
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center space-x-3">
                            <IconComponent className="w-6 h-6 text-blue-500" />
                            <CardTitle className="text-xl font-semibold text-gray-900">
                              {card.title}
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-600 leading-relaxed">
                            {card.description}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )} */}
          </>
        )}

        {/* Class Dashboard Content */}
        {dashboardType === 'class' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* First Class Section */}
              <div className="border-r border-gray-200 pr-3">
                <h2 className="text-2xl font-bold mb-4 text-blue-600">Class 1</h2>
                
                {/* Search Section */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Select Class</h3>
                    {classDashboardData && (
                      <button
                        onClick={() => downloadClassDashboard(classDashboardData, 1)}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                      >
                        <Download size={18} />
                        Download Excel
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-2">Class</label>
                      <select
                        value={classSearchData.classId}
                        onChange={(e) => setClassSearchData({ classId: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">Select a class</option>
                        {allClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.class_name || cls.name || `Class ${cls.title}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleClassSearch}
                      disabled={loading}
                      className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2 justify-center"
                    >
                      <Search size={20} />
                      {loading ? 'Loading...' : 'Load Dashboard'}
                    </button>
                  </div>
                  {error && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
                      <AlertCircle size={20} />
                      {error}
                    </div>
                  )}
                </div>

                {/* Class Dashboard Data */}
                {classDashboardData && (
                  <>
                    {renderClassInfo(classDashboardData, 'CLASS 1 DASHBOARD')}
                    {renderTopicPerformanceChart(classDashboardData)}
                    {renderClassUserAnswersSummary(classDashboardData, 1)}
                  </>
                )}

                {/* Default message when no class selected */}
                {!classDashboardData && !loading && (
                  <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <BookOpen className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Select First Class</h3>
                    <p className="text-sm text-gray-600">
                      Choose a class to view its performance metrics.
                    </p>
                  </div>
                )}
              </div>

              {/* Second Class Section */}
              <div className="pl-3">
                <h2 className="text-2xl font-bold mb-4 text-green-600">Class 2</h2>
                
                {/* Search Section */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Select Class</h3>
                    {classDashboardData2 && (
                      <button
                        onClick={() => downloadClassDashboard(classDashboardData2, 2)}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                      >
                        <Download size={18} />
                        Download Excel
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-2">Class</label>
                      <select
                        value={classSearchData2.classId}
                        onChange={(e) => setClassSearchData2({ classId: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">Select a class</option>
                        {allClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.class_name || cls.name || `Class ${cls.title}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleClassSearch2}
                      disabled={loading2}
                      className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2 justify-center"
                    >
                      <Search size={20} />
                      {loading2 ? 'Loading...' : 'Load Dashboard'}
                    </button>
                  </div>
                  {error2 && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
                      <AlertCircle size={20} />
                      {error2}
                    </div>
                  )}
                </div>

                {/* Class Dashboard Data */}
                {classDashboardData2 && (
                  <>
                    {renderClassInfo(classDashboardData2, 'CLASS 2 DASHBOARD')}
                    {renderTopicPerformanceChart(classDashboardData2)}
                    {renderClassUserAnswersSummary(classDashboardData2, 2)}
                  </>
                )}

                {/* Default message when no class selected */}
                {!classDashboardData2 && !loading2 && (
                  <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <BookOpen className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Select Second Class</h3>
                    <p className="text-sm text-gray-600">
                      Choose a class to compare with the first class.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;